// File: frontend/src/utils/adminUtils.ts
import { UserStatusEnum } from "../models/enums/UserStatusEnum";
import { UserRoleEnum } from "../models/enums/UserRoleEnum";
import { formatDateTime } from "./dateUtils";
import { ADMIN_CONTENT_PREVIEW_MAX_LENGTH } from "../constants";

/**
 * Clock time for the "Son yenileme: ..." label next to every admin refresh button.
 *
 * toLocaleTimeString() with no arguments follows the *browser* locale, not the
 * app's, so an en-US browser rendered the admin panel's timestamps as "5:28:20 PM"
 * while the rest of the panel was in Turkish 24-hour time. Pinning the locale and
 * hour12 here keeps every refresh label on the same clock.
 */
export const formatRefreshTime = (date: Date): string =>
  date.toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

export const formatAdminDate = (val: string | null): string => {
  if (!val) return "—";
  return formatDateTime(val);
};

/**
 * Whole-day difference between two ISO date-time strings (to - from), for the
 * "Fark (Gün)" columns in the Gündem admin panel — how many days between a
 * suggestion being submitted and it being accepted/rejected. Returns "—" when
 * either date is missing (e.g. a still-pending suggestion, or a topic with no
 * originating suggestion).
 */
export const formatDaysDiff = (
  fromIso: string | null | undefined,
  toIso: string | null | undefined
): string => {
  if (!fromIso || !toIso) return "—";
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  // Floor (not round) to match the backend's TIMESTAMPDIFF truncation —
  // otherwise a 12h+ diff would display 1 while the sort treats it as 0.
  const days = Math.floor((to - from) / (1000 * 60 * 60 * 24));
  return String(days);
};

export const truncateText = (
  text: string,
  max = ADMIN_CONTENT_PREVIEW_MAX_LENGTH
): string => (text.length <= max ? text : text.substring(0, max) + "…");

export const getStatusColor = (status: UserStatusEnum): string => {
  const colors: Partial<Record<UserStatusEnum, string>> = {
    [UserStatusEnum.ACTIVE]: "status-author",
    [UserStatusEnum.SUSPENDED]: "status-suspended",
    [UserStatusEnum.BANNED]: "status-banned",
    [UserStatusEnum.FROZEN_BY_USER]: "status-frozen",
    [UserStatusEnum.DELETED_BY_USER]: "status-deleted",
    [UserStatusEnum.DELETED_BY_ADMIN]: "status-deleted",
    [UserStatusEnum.INACTIVE]: "status-inactive",
  };
  return colors[status] ?? "status-default";
};

export const getRoleColor = (role: UserRoleEnum): string => {
  const colors: Record<UserRoleEnum, string> = {
    [UserRoleEnum.ADMIN]: "status-admin",
    [UserRoleEnum.MODERATOR]: "status-moderator",
    [UserRoleEnum.AUTHOR]: "status-author",
    [UserRoleEnum.ROOKIE]: "status-rookie",
  };
  return colors[role] ?? "status-rookie";
};